import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { FormatSelector } from "@/components/agenda-builder/FormatSelector";
import { SectionSelector } from "@/components/agenda-builder/SectionSelector";
import { OrderSummary } from "@/components/agenda-builder/OrderSummary";
import { AgendaCustomizer } from "@/components/agenda-customizer";
import { FORMATS, SECTIONS } from "@/data/agendaData";
import { Sparkles } from "lucide-react";
import { AgendaCustomizationState } from "@/types/agendaCustomization";

const AgendaBuilder = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedFormatId, setSelectedFormatId] = useState<
    number | undefined
  >();
  const [selectedSectionIds, setSelectedSectionIds] = useState<number[]>([]);
  const [customizations, setCustomizations] = useState<AgendaCustomizationState>({
    coverStyle: "premium",
    hasEngraving: false,
  });
  const [customizationPrice, setCustomizationPrice] = useState<number>(0);

  const selectedFormat = FORMATS.find((f) => f.id === selectedFormatId);
  const selectedSections = SECTIONS.filter((s) =>
    selectedSectionIds.includes(s.id)
  );

  // Manejador de cambios en personalización
  const handleCustomizationChange = (
    newCustomizations: AgendaCustomizationState,
    totalPrice: number
  ) => {
    setCustomizations(newCustomizations);
    // El totalPrice ya incluye el basePrice, así que restamos para obtener solo el costo de customización
    setCustomizationPrice(totalPrice - (selectedFormat?.basePrice || 0));
  };

  const handleAddToCart = () => {
    // TODO: Implementar integración con carrito
    const totalPrice =
      (selectedFormat?.basePrice || 0) +
      selectedSections.reduce((sum, s) => sum + s.price, 0) +
      customizationPrice;

    toast({
      title: "¡Agenda personalizada creada!",
      description: `${selectedFormat?.name} con ${selectedSections.length} secciones y personalizaciones agregada al carrito.`,
    });

    console.log("Configuración de agenda:", {
      format: selectedFormat,
      sections: selectedSections,
      customizations,
      customizationPrice,
      total: totalPrice,
    });

    // navigate("/cart");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-6 w-6 text-primary" />
              <h1 className="text-3xl md:text-4xl font-bold">
                Crea tu Agenda Personalizada
              </h1>
            </div>
            <p className="text-muted-foreground text-lg">
              Diseña una agenda única que se adapte perfectamente a tu estilo de
              vida
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Selectors */}
          <div className="lg:col-span-2 space-y-8">
            <FormatSelector
              formats={FORMATS}
              selectedFormatId={selectedFormatId}
              onFormatSelect={setSelectedFormatId}
            />

            {selectedFormatId && (
              <SectionSelector
                sections={SECTIONS}
                selectedSectionIds={selectedSectionIds}
                onSectionsChange={setSelectedSectionIds}
              />
            )}

            {/* Personalización Visual - Solo mostrar si hay formato y secciones seleccionadas */}
            {selectedFormatId && selectedSectionIds.length > 0 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    3. Personaliza el Diseño
                  </h2>
                  <p className="text-muted-foreground">
                    Elige el estilo de cubierta, diseño y agrega grabados personalizados
                  </p>
                </div>

                <AgendaCustomizer
                  basePrice={selectedFormat?.basePrice || 0}
                  onCustomizationChange={handleCustomizationChange}
                />
              </div>
            )}
          </div>

          {/* Right Column - Order Summary (Sticky) */}
          <div className="lg:col-span-1">
            <OrderSummary
              selectedFormat={selectedFormat}
              selectedSections={selectedSections}
              customizationPrice={customizationPrice}
              onAddToCart={handleAddToCart}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgendaBuilder;
